import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getStoreByOwner, createStore, updateStore } from '../lib/firebaseService';
import { Store } from 'lucide-react';

export default function StoreInfo() {
  const { firebaseUser } = useAuth();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [formData, setFormData] = useState({
    store_name: '',
    city: '',
    address: '',
    google_maps_link: '',
    whatsapp_number: ''
  });

  useEffect(() => {
    if (firebaseUser) {
      loadStore();
    }
  }, [firebaseUser]);

  const loadStore = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      if (userStore) {
        setStore(userStore);
        setFormData({
          store_name: userStore.store_name || '',
          city: userStore.city || '',
          address: userStore.address || '',
          google_maps_link: userStore.google_maps_link || '',
          whatsapp_number: userStore.whatsapp_number || ''
        });
      }
    } catch (error) {
      console.error('Mağaza yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'whatsapp_number') {
      let phone = value;
      if (!phone.startsWith('+994')) {
        phone = '+994';
      }
      const numbers = phone.slice(4).replace(/\D/g, '');
      const limitedNumbers = numbers.slice(0, 9);
      setFormData({ ...formData, [name]: '+994' + limitedNumbers });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.store_name.trim()) {
      toast.error('Mağaza adı tələb olunur');
      return;
    }

    if (!formData.whatsapp_number || formData.whatsapp_number.length < 13) {
      toast.error('Düzgün WhatsApp nömrəsi daxil edin');
      return;
    }

    setLoading(true);
    try {
      if (store) {
        await updateStore(store.id, formData);
        toast.success('Məlumatlar yeniləndi');
      } else {
        const newStoreData = {
          ...formData,
          owner_uid: firebaseUser.uid,
          store_type: 'retail',
          is_active: true
        };
        await createStore(newStoreData);
        toast.success('Mağaza yaradıldı');
        navigate('/dashboard');
      }
      loadStore();
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Store className="w-8 h-8" />
            Mağaza Məlumatları
          </h1>
          <p className="text-muted-foreground mt-1">Əsas mağaza məlumatları</p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="store_name">Mağaza Adı *</Label>
              <Input
                id="store_name"
                name="store_name"
                value={formData.store_name}
                onChange={handleChange}
                required
                placeholder="Mağazanızın adı"
                className="h-11"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Şəhər</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Bakı"
                  className="h-11"
                />
              </div>

              <div>
                <Label htmlFor="address">Ünvan</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="28 May küç. 5"
                  className="h-11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="google_maps_link">Google Maps Linki</Label>
              <Input
                id="google_maps_link"
                name="google_maps_link"
                type="url"
                value={formData.google_maps_link}
                onChange={handleChange}
                placeholder="https://maps.google.com/..."
                className="h-11"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Google Maps-da konumunuzu tapın və linki buraya yapışdırın
              </p>
            </div>

            <div>
              <Label htmlFor="whatsapp_number">WhatsApp Nömrəsi *</Label>
              <Input
                id="whatsapp_number"
                name="whatsapp_number"
                value={formData.whatsapp_number}
                onChange={handleChange}
                required
                placeholder="+994501234567"
                className="h-11"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Sifarişlər bu nömrəyə gələcək. Ölkə kodu ilə daxil edin.
              </p>
            </div>

            <div className="pt-4">
              <Button type="submit" className="w-full h-11" disabled={loading}>
                {loading ? 'Saxlanılır...' : (store ? 'Yenilə' : 'Mağaza yarat')}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Qeyd:</strong> Bu məlumatlar mağazanızın public səhifəsində görünəcək.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
